import React, { useState, type JSX } from 'react';
import { Box, Radio, Typography, TextField, InputAdornment, Stack, Checkbox } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { CustomTransComponent } from '../../../components/CustomTransComponent';
import SearchIcon from '@mui/icons-material/Search';
import { useTypedSelector } from '@fiji/common/src/app/store';
import { selectedOrg } from '@fiji/common/src/features/orgManagement/orgSlice';
import { Hierarchy, renderGroupHierarchyIcons } from '../../../components';
import { useGetAllGroupsMutation } from '@fiji/common/src/features/group/groupApi';
import { GroupHierarchySkeleton } from 'pages/GroupHierarchy/GroupHierarchySkeleton';
import { useSearchDeviceOrGroup } from '@fiji/common/src/hooks';

interface DevicesListProps {
    selectedDeviceId?: any;
    onDeviceSelect: (deviceId: string, deviceData: any) => void;
    selectionType?: 'radio' | 'checkbox';
}

export const DevicesList: React.FC<DevicesListProps> = (props) => {
    const { t } = useTranslation();
    const { selectedDeviceId, onDeviceSelect, selectionType } = props;
    const currentOrg = useTypedSelector(selectedOrg);
    const [searchKey, setSearchKey] = useState('');
    const [groupsData, setGroupsData] = React.useState<any>();
    const [hierarchyMappingData, setHierarchyMappingData] = React.useState<any>({});
    const [loadingHierarchyNode, setLoadingHierarchyNode] = React.useState<string[]>([]);
    const normalizedSearchKey = searchKey.trim();
    const shouldSearch = normalizedSearchKey.length > 0;

    const normalizeNodeIcon = (iconData: any): any => {
        if (iconData?.web) {
            return iconData;
        }

        if (iconData?.status?.icon?.web) {
            return iconData.status.icon;
        }

        if (iconData?.icon?.web) {
            return iconData.icon;
        }

        return { web: { family: 'material-ui', name: 'StorageOutlined' } };
    };

    const { currentData: filteredRecords, isLoading: searchLoader } = useSearchDeviceOrGroup({
        searchKey: shouldSearch ? normalizedSearchKey : undefined,
    });

    const [getAllGroups, { data: groupsHierarchy, isLoading: isGroupsLoading }] = useGetAllGroupsMutation();

    React.useEffect(() => {
        if (!groupsData && groupsHierarchy?.data?.records) {
            const records = groupsHierarchy.data.records.map((r: any) => ({
                ...r,
                type: r.type || 'GROUP',
                icon: normalizeNodeIcon(r?.icon),
            }));
            setGroupsData(records);
        }
    }, [groupsHierarchy]);

    React.useEffect(() => {
        if (currentOrg?.id) {
            void getAllGroups({});
        }
    }, [currentOrg]);

    const handleFetchHierarchy = async (parentData: any): Promise<void> => {
        setLoadingHierarchyNode((prevState) => [...prevState, parentData?.id]);

        try {
            const { data: childHierarchyData }: any = await getAllGroups({ parent: parentData?.id });
            if (childHierarchyData) {
                const records = childHierarchyData?.data?.records?.map((r: any) => ({
                    ...r,
                    type: r.type || (r.deviceCount !== undefined ? 'GROUP' : 'DEVICE'),
                    icon: normalizeNodeIcon(r?.icon),
                }));
                setHierarchyMappingData((prevState: any) => ({
                    ...prevState,
                    [parentData?.id]: records,
                }));
            }
        } catch (error) {
            console.error('Failed to fetch hierarchy data', error);
        } finally {
            setLoadingHierarchyNode((prevState) => prevState.filter((loadingNode) => loadingNode !== parentData?.id));
        }
    };

    const selectionHandler = (selectedNodeData: any): void => {
        if (selectedNodeData.type === 'DEVICE') {
            const rawPath = selectedNodeData.groupPath || selectedNodeData.path;
            let breadcrumbs: string[] = [];
            if (rawPath) {
                breadcrumbs = [...rawPath];
            }
            onDeviceSelect(selectedNodeData.id, {
                ...selectedNodeData,
                breadcrumbs,
            });
        }
    };

    const renderCustomIcons = (
        treeItem: any,
        itemSelectionType: any,
        selectedNodes: any,
        hierarchyTheme: any,
        labelColor: any
    ): any => renderGroupHierarchyIcons(treeItem, itemSelectionType, selectedNodes, hierarchyTheme, labelColor);

    const getGroupList = (): JSX.Element =>
        groupsData ? (
            <Stack spacing={2} sx={{ height: '100%', overflow: 'hidden' }}>
                <TextField
                    fullWidth
                    size="small"
                    placeholder={t('COMMON:SEARCH_PLACEHOLDER')}
                    value={searchKey}
                    onChange={(e) => setSearchKey(e.target.value)}
                    sx={{
                        backgroundColor: '#fff',
                        '& .MuiOutlinedInput-root': {
                            backgroundColor: '#fff',
                        },
                        mb: 1,
                    }}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
                            </InputAdornment>
                        ),
                    }}
                />
                <Box sx={{ flexGrow: 1, overflowY: 'auto' }}>
                    <Hierarchy
                        isLoading={!groupsData || searchLoader}
                        data={
                            shouldSearch
                                ? filteredRecords?.filter((r: any) => r.type !== 'LOAD')
                                : currentOrg?.defaultGroupAccess
                                ? [
                                      {
                                          ...currentOrg,
                                          name: currentOrg?.name,
                                          groupCount: currentOrg?.groupCount,
                                          deviceCount: currentOrg?.deviceCount,
                                          type: 'ORGANIZATION',
                                          icon: { web: { family: 'material-ui', name: 'CorporateFare' } },
                                      },
                                  ]
                                : groupsData
                        }
                        hasChild={(treeItem: any): boolean => treeItem?.groupCount > 0 || treeItem?.deviceCount > 0}
                        labelKey={'name'}
                        hierarchyMappingData={hierarchyMappingData}
                        loadingNode={loadingHierarchyNode}
                        handleTreeNodeSelection={selectionHandler}
                        getAvatarChild={renderCustomIcons}
                        autoChildSelection={false}
                        loadMore={handleFetchHierarchy}
                        loadingSkeleton={<GroupHierarchySkeleton row={1} />}
                        selectedNodes={selectionType === 'checkbox' ? selectedDeviceId || [] : selectedDeviceId}
                        selectionType={selectionType || 'radio'}
                        actions={({ treeItem }: any) => {
                            if (treeItem?.type === 'DEVICE') {
                                if (selectionType === 'checkbox') {
                                    return (
                                        <Checkbox
                                            checked={
                                                Array.isArray(selectedDeviceId) &&
                                                selectedDeviceId.includes(treeItem.id)
                                            }
                                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                                e.stopPropagation();
                                                onDeviceSelect(treeItem.id, treeItem);
                                            }}
                                            onClick={(e: React.MouseEvent<HTMLButtonElement>) => e.stopPropagation()}
                                        />
                                    );
                                }
                                return (
                                    <Radio
                                        checked={selectedDeviceId === treeItem.id}
                                        value={treeItem.id}
                                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                            e.stopPropagation();
                                            onDeviceSelect(treeItem.id, treeItem);
                                        }}
                                        onClick={(e: React.MouseEvent<HTMLButtonElement>) => e.stopPropagation()}
                                    />
                                );
                            }
                            return <React.Fragment />;
                        }}
                        filters={[
                            {
                                key: 'type',
                                operator: '!==',
                                value: 'DEVICE',
                                action: 'disableCheck',
                            },
                        ]}
                    />
                </Box>
            </Stack>
        ) : isGroupsLoading ? (
            <Box flexGrow={1} display="flex" alignItems="center" justifyContent="center">
                <GroupHierarchySkeleton row={1} />
            </Box>
        ) : (
            <Box flexGrow={1} display="flex" alignItems="center" justifyContent="center">
                <Typography variant="body2" color="text.secondary">
                    <CustomTransComponent translationKey={'WIDGETS:BALANCING.EMPTY_DEVICES'} />
                </Typography>
            </Box>
        );

    return (
        <Box sx={{ height: '100%', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
            {getGroupList()}
        </Box>
    );
};
